// A reading against the band it is judged by, over time (console R-357).
//
// It lives beside `Chart` and shares its lazy seam (D-071/R-105): chart.js is
// heavy and most screens have no chart, so the implementation is split out and
// pulled in only when one renders.
//
// It is a SEPARATE component rather than options on `Chart`, and deliberately so.
// `Chart` is the category-scale summary chart the Cockpit and the sensor trend
// use — labels in, series out, no axis maths. This one needs a numeric time
// axis, a shaded step-function underneath, per-segment colouring and
// drag-to-select. Folding all of that into `Chart` would have made every
// existing caller pay for machinery it does not use, and would have put four
// concerns behind one prop bag.
import { lazy, Suspense } from 'preact/compat';

/** One span of the band ladder — `[from_ms, to_ms)`, from the backend verbatim. */
export interface RangeBand {
  from_ms: number;
  to_ms: number;
  /** `false` = the parameter is NOT judged here (night, for most channels).
   *  Nothing is shaded: carrying the day band across would draw a rule the crop
   *  does not state and the alarm plane does not apply. */
  applicable: boolean;
  crit_low: number | null;
  orange_low: number | null;
  optimal_min: number | null;
  optimal_max: number | null;
  orange_high: number | null;
  crit_high: number | null;
}

export interface RangePoint {
  ts_ms: number;
  avg: number;
}

/** How the x-axis labels an instant. Two lines — date over time — because a
 *  window can span days and a bare clock time cannot say which one. */
export interface TimeLabel {
  date: string;
  time: string;
}

/** A run the SERVER classified as out of band. The chart colours the line from
 *  this rather than re-deriving it — the backend's classifier is the one that
 *  raised the alarm, and a second opinion here could contradict it. */
export interface RangeBreach {
  from_ms: number;
  to_ms: number;
  zone: 'orange_low' | 'orange_high' | 'critical_low' | 'critical_high';
}

export interface RangeChartProps {
  points: RangePoint[];
  bands: RangeBand[];
  breaches: RangeBreach[];
  /** Axis label; points and bands are already in it. */
  unit: string;
  /** The reading line's colour — a fixed per-parameter hex, never by rank. */
  color: string;
  /** Label for the reset control the chart shows while it is zoomed. */
  resetZoomLabel: string;
  /**
   * ⚠ A drag zooms THIS CHART ONLY, locally, and refetches nothing.
   *
   * It used to lift the dragged range up to the page, which refetched the whole
   * payload and re-scoped every other card. Two things were wrong with that:
   * dragging on Temperature silently re-ranged EC and pH, and when the dragged
   * range landed in a gap in the readings EVERY card went blank at once — a
   * gesture on one chart emptying the screen.
   *
   * The two ideas are now separate. The WINDOW control in the filter row is the
   * DATA filter: it is shared, it scopes every card and the export, and the
   * numbers on screen always agree with each other. A drag is a VIEW zoom: it
   * is local, instant, needs no server round-trip, and is undone by the reset
   * this chart renders for itself.
   */
  onZoomChange?: (zoomed: boolean) => void;
  /** Formats an x tick as date + time. Supplied by the caller so locale lives in
   *  one place. */
  formatTime: (ms: number) => TimeLabel;
  /** Bucket width, so a real hole in the series breaks the line instead of
   *  being bridged by a diagonal that invents readings. */
  bucketMs: number;
  /** Taller when the card is expanded to full screen. */
  height?: number;
  /** Rendered in the empty state — "no readings in this window" reads very
   *  differently from a chart that failed to load. */
  emptyLabel: string;
}

const RangeChartImpl = lazy(() => import('./range-chart-impl').then((m) => ({ default: m.RangeChartImpl })));

export function RangeChart(props: RangeChartProps) {
  return (
    <Suspense fallback={<div style={{ height: `${props.height ?? 260}px`, width: '100%' }} />}>
      <RangeChartImpl {...props} />
    </Suspense>
  );
}
